/**
 * English content bundle — every user-facing string lives here.
 * Structure is mirrored 1:1 in de.js.
 */

const en = {
  code: 'en',
  htmlLang: 'en',
  ogLocale: 'en_US',
  intlLocale: 'en-GB',
  label: 'English',
  shortLabel: 'EN',

  common: {
    bookConsultation: 'Book a Consultation',
    bookCall: 'Book a Call',
    exploreServices: 'Explore Services',
    exploreTheServices: 'Explore the services',
    whatsapp: 'WhatsApp',
    whatsappLong: 'Message on WhatsApp',
    learnMore: 'Learn more',
    viewAllServices: 'View all services',
    inquireNow: 'Inquire now',
    email: 'Email',
    placeholder: 'Placeholder',
    or: 'or',
    scroll: 'Scroll',
    sending: 'Sending…',
    tryAgain: 'Try again',
  },

  whatsappMessage:
    "Hello Romilda, I found your website and I'd like to talk about my brand.",

  nav: {
    home: 'Home',
    services: 'Services',
    about: 'About',
    contact: 'Contact',
  },

  header: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    primaryNavLabel: 'Primary',
    mobileNavLabel: 'Mobile',
    languageGroupLabel: 'Language',
    switchTo: 'Switch to German',
  },

  footer: {
    tagline:
      'A premium digital marketing agency for brands with taste. Content strategy, social media growth and visual identity — built with editorial precision and measured on results.',
    navigate: 'Navigate',
    servicesHeading: 'Services',
    contactHeading: 'Contact',
    rights: 'All rights reserved.',
    madeBy: 'Made by',
    onInstagram: 'on Instagram',
  },

  statsBand: {
    eyebrow: 'The numbers',
  },

  stats: [
    {
      value: '500,000+',
      label: 'Views generated per month',
      note: 'Across managed accounts and campaigns',
      testId: 'home-stats-views',
    },
    {
      value: '160,000+',
      label: 'Accounts reached monthly',
      note: 'Organic reach, no paid shortcuts',
      testId: 'home-stats-reach',
    },
    {
      value: '100%',
      label: 'Tailored strategies',
      note: 'Never templated, never recycled',
      testId: 'home-stats-tailored',
    },
  ],

  marquee: [
    'Content Strategy',
    'Social Media Growth',
    'Visual Identity',
    'Editorial Direction',
    'Brand Positioning',
    'Digital Strategy',
  ],

  services: [
    {
      slug: 'social-media-management',
      number: '01',
      icon: 'Sparkles',
      title: 'Social Media Management',
      short:
        'Strategic account management, organic growth, and engagement strategy — handled end to end.',
      overview:
        'We take full ownership of your presence: the calendar, the copy, the community, the cadence. Every post is placed with intent, so your account compounds instead of coasting.',
      who: [
        'Founders whose calendars no longer allow for daily posting',
        'Brands with beautiful products and an inconsistent feed',
        'Teams ready to hand over the rhythm to someone who owns it',
      ],
      outcomes: [
        'A consistent posting rhythm that compounds month over month',
        'Organic reach and follower growth from the right audience',
        'Engagement that turns into enquiries, not just likes',
      ],
      includes: [
        'Monthly content calendar and scheduling',
        'Caption and copywriting in your brand voice',
        'Community management and engagement strategy',
        'Hashtag, keyword and discoverability strategy',
        'Monthly performance reporting with commentary',
      ],
    },
    {
      slug: 'content-strategy-aesthetic',
      number: '02',
      icon: 'LayoutTemplate',
      title: 'Content Strategy & Aesthetic',
      short:
        'High-end visual and copy direction, built so every piece of content earns its place.',
      overview:
        'Most brands do not have a content problem — they have a direction problem. We define the pillars, the visual language and the storytelling frameworks that make planning effortless and your feed unmistakable.',
      who: [
        'Brands whose content looks dated, scattered or off-brand',
        'Businesses posting consistently with nothing to show for it',
        'Founders who want taste and structure in equal measure',
      ],
      outcomes: [
        'A clear, ownable content direction you can brief against',
        'Content pillars that make planning quick instead of painful',
        'A feed that reads as intentional, editorial and premium',
      ],
      includes: [
        'Content pillar framework',
        'Visual direction and moodboard',
        'Editorial photo and video art direction',
        'Hook, caption and storytelling frameworks',
        'A 30-day content plan you can execute immediately',
      ],
    },
    {
      slug: 'visual-identity-branding',
      number: '03',
      icon: 'Gem',
      title: 'Visual Identity & Branding',
      short:
        'Complete brand development — positioning, logo suite, palette and guidelines.',
      overview:
        'An identity is not a logo. It is the reason someone decides you are worth more before you have said a word. We build the full system, then hand you the guidelines to protect it.',
      who: [
        'New brands launching and wanting to launch properly',
        'Established brands that have outgrown their original look',
        'Personal brands stepping into a more premium positioning',
      ],
      outcomes: [
        'A cohesive identity you are genuinely proud to show',
        'Instant recognisability across every touchpoint',
        'The visual permission to charge premium prices',
      ],
      includes: [
        'Brand positioning and messaging foundation',
        'Logo suite and typography system',
        'Colour palette and art direction',
        'Brand guidelines document',
        'Social media brand kit and templates',
      ],
    },
    {
      slug: 'digital-strategy',
      number: '04',
      icon: 'LineChart',
      title: 'Digital Strategy',
      short:
        'Data-driven marketing plans and performance tracking — decisions made on evidence.',
      overview:
        'Taste without numbers is decoration. We map the audience, the channels and the funnel, then define the metrics that actually indicate growth — so every quarter is a decision, not a guess.',
      who: [
        'Brands ready to scale beyond instinct and improvisation',
        'Teams unsure which channels deserve time and budget',
        'Founders who want a documented roadmap, not vague advice',
      ],
      outcomes: [
        'A documented growth roadmap with clear priorities',
        'Clarity on which channels earn their place',
        'Decisions grounded in data you can actually read',
      ],
      includes: [
        'Audience and competitor research',
        'Channel and funnel strategy',
        'Campaign planning and launch calendar',
        'KPI framework and reporting dashboard',
        'Quarterly strategy reviews',
      ],
    },
    {
      slug: 'websites-ai-automation',
      number: '05',
      icon: 'Cpu',
      title: 'Websites & AI Automation',
      short:
        'Custom websites, web apps and AI automation — built with our technology partner CloudService.hr.',
      overview:
        'Visibility is only half the distance. Sooner or later a brand needs a place where attention turns into an enquiry — and systems that keep working long after you have gone offline. That is what we build in partnership with CloudService.hr: custom websites, web and mobile apps, AI assistants and automation. No templates, no page builders.',
      who: [
        'Brands ready to turn a beautiful presence into reliable enquiries',
        'Companies still doing the same tasks by hand every single day',
        'Founders with a product idea that needs a real MVP',
      ],
      outcomes: [
        'A website that belongs to your brand — not to a template',
        'AI and automation absorbing the routine: enquiries, quotes, follow-ups',
        'A product that actually goes live — in weeks, not quarters',
      ],
      includes: [
        'Custom website, landing page or web app',
        'AI assistants, advisors and chat systems',
        'Automation for quotes, leads and follow-ups',
        'CRM and admin dashboards for your team',
        'iOS and Android apps, plus SaaS platforms',
        'Payment flows, hosting and monitoring — production ready',
      ],
    },
  ],

  /** value = canonical English string stored in the database. */
  serviceOptions: [
    { value: 'Social Media Management', label: 'Social Media Management', group: 'marketing' },
    { value: 'Content Strategy & Aesthetic', label: 'Content Strategy & Aesthetic', group: 'marketing' },
    { value: 'Visual Identity & Branding', label: 'Visual Identity & Branding', group: 'marketing' },
    { value: 'Digital Strategy', label: 'Digital Strategy', group: 'marketing' },
    { value: 'Website', label: 'Website', group: 'tech' },
    { value: 'Web App', label: 'Web app', group: 'tech' },
    { value: 'Mobile App', label: 'Mobile app (iOS / Android)', group: 'tech' },
    { value: 'AI Tool', label: 'AI tool / AI assistant', group: 'tech' },
    { value: 'Automation', label: 'Automation', group: 'tech' },
    { value: 'CRM / Admin Dashboard', label: 'CRM / admin dashboard', group: 'tech' },
    { value: 'SaaS Platform', label: 'SaaS platform', group: 'tech' },
    { value: 'E-Commerce', label: 'Online shop / e-commerce', group: 'tech' },
    { value: 'Not sure yet', label: 'Not sure yet', group: 'other' },
  ],

  serviceOptionGroups: {
    marketing: 'Marketing & brand',
    tech: 'Websites & AI automation',
    other: 'Other',
  },

  /** These project types reveal the budget and timeline fields. */
  techProjectValues: [
    'Website',
    'Web App',
    'Mobile App',
    'AI Tool',
    'Automation',
    'CRM / Admin Dashboard',
    'SaaS Platform',
    'E-Commerce',
  ],

  budgetOptions: [
    { value: 'Under EUR 3,000', label: 'Under €3,000' },
    { value: 'EUR 3,000 - 10,000', label: '€3,000 – 10,000' },
    { value: 'EUR 10,000 - 25,000', label: '€10,000 – 25,000' },
    { value: 'EUR 25,000+', label: '€25,000 +' },
    { value: 'Budget open', label: 'Still open' },
  ],

  timelineOptions: [
    { value: 'ASAP (within 2 weeks)', label: 'As soon as possible (within 2 weeks)' },
    { value: 'Within 1 month', label: 'Within a month' },
    { value: '1-3 months', label: '1 – 3 months' },
    { value: '3+ months', label: '3+ months' },
    { value: 'Timeline open', label: 'Still open — advice welcome' },
  ],

  partnership: {
    eyebrow: 'Technology partnership',
    title: 'Once the brand stands, we build the product.',
    lede:
      'For everything that needs code, I work with CloudService.hr — an AI and automation studio based in Vienna and Zagreb. I bring the brand, the words and the aesthetic; the studio brings the engineering. You keep one point of contact: me.',
    partnerLabel: 'Technology partner',
    partnerName: 'CloudService.hr',
    partnerTagline: 'AI Solutions · SaaS Platforms · Mobile Apps · Business Automation',
    partnerClaim: 'We build digital products, not just websites.',
    partnerUrl: 'https://cloudservice.hr',
    visitPartner: 'Visit CloudService.hr',
    capabilities: [
      {
        icon: 'BrainCircuit',
        title: 'AI solutions',
        body: 'AI systems that take over real processes — advisors, assistants, document intelligence.',
      },
      {
        icon: 'Boxes',
        title: 'SaaS platforms',
        body: 'From login to subscription: production-ready platforms, not prototypes.',
      },
      {
        icon: 'Smartphone',
        title: 'Mobile apps',
        body: 'iOS and Android that feel like consumer apps — not like internal tools.',
      },
      {
        icon: 'Workflow',
        title: 'Business automation',
        body: 'Internal tools and automation that give your team its hours back.',
      },
    ],
    referencesLabel: 'Live in production',
    references: [
      {
        name: 'DealAlert.ai',
        url: 'https://dealalert.ai',
        tag: 'AI SaaS · Asset intelligence',
        body: 'AI spots car and property listings priced below market — with its own iOS app and Stripe billing.',
      },
      {
        name: 'HausDesHandys.at',
        url: 'https://hausdeshandys.at',
        tag: 'Retail · AI commerce',
        body: 'A local repair business fully digitised: repair configurator, smart pricing, AI assistant and admin system.',
      },
      {
        name: 'NAAutomobile.at',
        url: 'https://naautomobile.at',
        tag: 'Automotive · Platform',
        body: 'Premium dealership platform with an AI photo studio, WhatsApp-first enquiries, financing and trade-in leads.',
      },
    ],
    note:
      'From first conversation to finished product: understand the idea, sharpen the concept, build the MVP, integrate AI and automation, go live — then keep improving.',
    cta: 'Discuss a project',
    liveLabel: 'View live',
  },

  valueProps: [
    {
      icon: 'Compass',
      title: 'Tailored strategy',
      body: 'No recycled decks. Every strategy is built from your audience, your market and your ambition.',
    },
    {
      icon: 'Aperture',
      title: 'A luxury, editorial eye',
      body: 'Taste is a business asset. We art-direct your brand the way a magazine directs a cover story.',
    },
    {
      icon: 'TrendingUp',
      title: 'Results-driven execution',
      body: 'Beautiful is the baseline. We measure reach, engagement and enquiries — and act on what we find.',
    },
    {
      icon: 'Layers',
      title: 'Brand building, not posting',
      body: 'We build recognisable brands with a point of view, not accounts that simply stay active.',
    },
    {
      icon: 'Handshake',
      title: 'A genuinely personal approach',
      body: 'You work directly with Romilda. Small roster, close attention, real accountability.',
    },
  ],

  process: [
    {
      step: '01',
      title: 'Discover',
      body: 'We start with a consultation: your audience, your market, your positioning and what has held growth back so far.',
    },
    {
      step: '02',
      title: 'Strategize',
      body: 'We define the content pillars, the visual direction and the channel plan — documented so nothing lives on instinct.',
    },
    {
      step: '03',
      title: 'Create',
      body: 'We produce and art-direct the work: copy, visuals, campaigns. Every asset ladders back to the strategy.',
    },
    {
      step: '04',
      title: 'Grow',
      body: 'We measure, refine and scale what performs — with reporting that reads clearly and reviews that move things forward.',
    },
  ],

  caseStudies: [
    {
      tag: 'Engagement growth',
      metric: '+ / –',
      title: 'From quiet feed to consistent conversation',
      body: 'A content pillar rebuild and posting rhythm designed to move an audience from passive scrolling to active engagement.',
      status: 'Case study in progress',
    },
    {
      tag: 'Reach improvement',
      metric: '+ / –',
      title: 'Reaching the right rooms, organically',
      body: 'Discoverability and format strategy focused on qualified reach — the accounts that actually convert into enquiries.',
      status: 'Case study in progress',
    },
    {
      tag: 'Brand positioning',
      metric: '+ / –',
      title: 'A repositioning that raised the price point',
      body: 'Identity, messaging and art direction rebuilt so the brand could confidently occupy a more premium tier.',
      status: 'Case study in progress',
    },
    {
      tag: 'Visual transformation',
      metric: 'Before / After',
      title: 'A feed rebuilt as an editorial spread',
      body: 'A full visual overhaul: palette, composition and grid rhythm treated as one continuous editorial narrative.',
      status: 'Case study in progress',
    },
  ],

  testimonials: [
    {
      quote:
        'Romilda did not just make our content look better — she gave us a point of view. For the first time our brand feels like it belongs at the price we charge.',
      name: 'Client name',
      role: 'Founder, brand category',
      placeholder: true,
    },
    {
      quote:
        'The strategy was specific, documented and genuinely ours. Within weeks our reach was climbing and, more importantly, the right people were enquiring.',
      name: 'Client name',
      role: 'Marketing lead, brand category',
      placeholder: true,
    },
    {
      quote:
        'Working with her feels like having a creative director and a strategist in the same conversation. Nothing is left to guesswork, nothing looks accidental.',
      name: 'Client name',
      role: 'Owner, brand category',
      placeholder: true,
    },
    {
      quote:
        'She has an editorial eye you cannot teach, paired with a very clear head for numbers. That combination is rare and it shows in the results.',
      name: 'Client name',
      role: 'Co-founder, brand category',
      placeholder: true,
    },
    {
      quote:
        'We handed over an inconsistent account and got back a brand. The reporting alone changed how we make decisions internally.',
      name: 'Client name',
      role: 'Director, brand category',
      placeholder: true,
    },
  ],

  photo: {
    homeHeroAlt:
      'Romilda, founder of Romilda Marketing Hub, photographed in an editorial studio setting',
    homeHeroCaption: 'Romilda · Founder & Strategist',
    homeAboutAlt: 'Portrait of Romilda',
    homeAboutCaption: 'Founder · Strategist · Editorial eye',
    homeEditorialAlt: 'Editorial portrait of Romilda in a studio with geometric light',
    homeEditorialCaption: 'Editorial direction',
    servicesHeroAlt: 'Romilda photographed in a studio with geometric shadow',
    servicesHeroCaption: 'Strategy · Content · Identity · Growth',
    aboutHeroAlt: 'Romilda Bahollari, founder of Romilda Marketing Hub',
    aboutHeroCaption: 'Romilda · Founder',
    aboutPortraitAlt: 'Portrait of Romilda in a white shirt',
    aboutApproachAlt: 'Editorial portrait of Romilda against a studio wall',
    aboutApproachCaption: 'Strategy meets art direction',
    contactHeroAlt: 'Romilda, founder of Romilda Marketing Hub',
    contactHeroCaption: 'You’ll speak with Romilda directly',
  },

  home: {
    hero: {
      eyebrow: 'Premium digital marketing studio',
      titleBefore: 'Strategy that makes your brand ',
      titleAccent: 'impossible',
      titleAfter: ' to ignore.',
      lede:
        'Premium content strategy, social media growth and branding for modern businesses ready to elevate their presence — built with an editorial eye and measured on results.',
      statViews: 'monthly views generated',
      statReach: 'accounts reached',
      nowOffering: 'Now offering',
      aiTeaser: 'Romilda AI — instant strategy guidance',
    },
    services: {
      eyebrow: 'What we do',
      title: 'Five disciplines, one cohesive brand.',
      lede:
        'Every engagement is built around your positioning — not a package. Choose the discipline you need, or let us build the full system.',
    },
    about: {
      eyebrow: 'Meet Romilda',
      title: 'Marketing built on strategy — and finished with taste.',
      p1: 'I built Romilda Marketing Hub because I kept seeing the same gap: agencies that could report on numbers but had no eye, and creatives with beautiful work and no strategy behind it. Brands were being asked to choose.',
      p2: 'My work sits in the middle. I plan like a strategist — audience, positioning, funnels, data — and art-direct like an editor, treating every feed as a publication with a point of view. The result is a brand that looks considered and performs like it was engineered.',
      quote:
        'Taste is not decoration. It is the fastest way to signal that you are worth more.',
      readStory: 'Read the full story',
    },
    why: {
      eyebrow: 'Why work with us',
      title: 'The difference is in the thinking, not the template.',
      lede: 'A small roster, close attention and a standard we do not lower.',
      aiTag: 'New',
      aiTitle: 'Romilda AI',
      aiBody:
        'Ask our in-house strategy assistant for an instant read on your content, branding or growth — before you ever book a call.',
      aiHint: 'Open the widget, bottom right',
    },
    results: {
      eyebrow: 'Results',
      title: 'Growth we can point to.',
      lede:
        'Full case studies are being documented. Here is the shape of the work — each card will be replaced with client numbers and before/after visuals.',
      sideNote:
        'Every result starts the same way — a documented strategy, then art direction that makes it unmistakable.',
    },
    testimonials: {
      eyebrow: 'Trust',
      title: 'What clients say.',
      lede:
        'Placeholder quotes below — written in the tone we expect, ready to be replaced with real client words.',
    },
    process: {
      eyebrow: 'The process',
      title: 'How we work together.',
      lede: 'Four stages, clearly defined, so you always know what happens next.',
    },
    bottom: {
      prefix: 'Prefer to read first?',
      servicesLink: 'Explore the services',
      aboutLink: 'read Romilda’s story',
    },
  },

  servicesPage: {
    hero: {
      eyebrow: 'Services',
      titleLines: ['Five disciplines.', 'One elevated brand.'],
      lede:
        'Take one discipline or the full system. Either way, the strategy is documented, the art direction is deliberate, and the results are measured — never guessed.',
      indexNavLabel: 'Services index',
    },
    stage: {
      scrollCue: 'Scroll to explore',
      progressAria: 'Service progress',
      goTo: 'Go to {title}',
    },
    whoLabel: 'Who it’s for',
    outcomesLabel: 'Expected outcomes',
    includesLabel: 'What’s included',
    process: {
      eyebrow: 'The process',
      title: 'What working together looks like.',
      lede:
        'No matter which service you choose, the path is the same — clear, documented and collaborative.',
    },
    finalCta: {
      eyebrow: 'Next step',
      title: 'Not sure which service you need?',
      body:
        'Book a consultation and we will map it out together — or ask Romilda AI for an instant read before you commit to a call.',
    },
  },

  aboutPage: {
    hero: {
      eyebrow: 'About',
      titleLines: ['I build brands that look', 'as considered as they are.'],
      lede:
        'I’m Romilda — founder of Romilda Marketing Hub, a studio for brands that want strategy and taste in the same room. My work blends structured marketing thinking with high-fashion, editorial art direction.',
    },
    story: {
      eyebrow: 'The story',
      title: 'How the studio started.',
      p1: 'I started in marketing the way many people do — with spreadsheets, reports and campaign calendars. I learned quickly that I could grow numbers. What frustrated me was watching brands with real potential present themselves in a way that undercut everything they were worth.',
      p2: 'At the same time, I was drawn to fashion and editorial work — the way a magazine can communicate an entire worldview in a single spread. It became obvious that the two disciplines belonged together: the rigour of strategy, the confidence of good art direction.',
      p3: 'Romilda Marketing Hub exists to hold both. We plan properly, we look extraordinary, and we keep the two accountable to each other. Today the work reaches well over a hundred thousand accounts a month — not through shortcuts, but through consistency and a very high bar for what goes out.',
      quote:
        'I don’t want your brand to look like everyone else’s, performing slightly better. I want it to look like nobody else’s — and perform because of it.',
      cite: 'Romilda, Founder',
      numbersLabel: 'By the numbers',
    },
    philosophy: {
      eyebrow: 'Philosophy',
      title: 'Four beliefs that shape every engagement.',
      items: [
        {
          title: 'Strategy first, always',
          body: 'Before a single asset is made, we agree on who we are speaking to, what we want them to believe, and how we will know it worked. Creative without that foundation is expensive decoration.',
        },
        {
          title: 'Aesthetics are commercial',
          body: 'How a brand looks determines what it can charge. We treat art direction as a revenue lever, not a finishing touch — and we hold it to an editorial standard.',
        },
        {
          title: 'Consistency beats intensity',
          body: 'A brand is built by hundreds of aligned decisions, not one viral moment. We design systems you can sustain, then refine them relentlessly.',
        },
        {
          title: 'Fewer clients, closer attention',
          body: 'The roster stays small on purpose. You work directly with me — not an account manager relaying decisions through three inboxes.',
        },
      ],
    },
    approach: {
      eyebrow: 'What to expect',
      title: 'Clear, close and held to a standard.',
      lede:
        'Working with a small studio should feel different from working with an agency. Here is what that means in practice.',
      items: [
        'A documented strategy you can actually brief against',
        'Art direction held to an editorial standard',
        'Clear reporting with commentary, not raw dashboards',
        'Direct access to Romilda throughout the engagement',
        'Honest recommendations — including when to do less',
      ],
      cta: 'Start with a consultation',
    },
    finalCta: {
      eyebrow: 'Let’s talk',
      title: 'If this sounds like the way you want to work — let’s begin.',
      body:
        'Book a consultation and bring your current challenge. You will leave the call with a clearer direction, whether or not we work together.',
    },
  },

  contactPage: {
    hero: {
      eyebrow: 'Contact',
      titleLines: ['Let’s build something', 'worth looking at.'],
      lede:
        'Tell us a little about your brand and where you feel stuck. You will leave the consultation with a clearer direction — whether or not we end up working together.',
      emailLabel: 'Email',
      whatsappLabel: 'WhatsApp',
      responseLabel: 'Response time',
      responseValue: 'Within 2 working days',
    },
    form: {
      eyebrow: 'Consultation enquiry',
      title: 'Tell us about your brand.',
      lede:
        'A few short questions. The more specific you are, the more useful the first conversation will be.',
      nameLabel: 'Your name *',
      namePlaceholder: 'Elena Rossi',
      brandLabel: 'Business / brand name',
      brandPlaceholder: 'Casa Rossi',
      emailLabel: 'Email *',
      emailPlaceholder: 'you@yourbrand.com',
      serviceLabel: 'Service of interest',
      servicePlaceholder: 'Select a service',
      budgetLabel: 'Budget range',
      budgetPlaceholder: 'Select a budget',
      timelineLabel: 'Timeline',
      timelinePlaceholder: 'Select a timeline',
      projectHint:
        'Websites, apps and automation are built together with our technology partner CloudService.hr. Budget and timeline help us come back with a realistic first assessment.',
      challengeLabel: 'Your biggest current challenge *',
      challengePlaceholder:
        'For example: our Instagram looks inconsistent and we get almost no enquiries from it.',
      privacy: 'Your details are used only to reply to this enquiry.',
      submit: 'Send enquiry',
      errors: {
        name: 'Please tell us your name.',
        email: 'Please enter a valid email address.',
        challenge: 'A sentence or two about your challenge helps us prepare.',
        incomplete: 'Please complete the highlighted fields.',
        failed: 'We could not send that just now. Please email us directly.',
      },
      toastSuccess: 'Enquiry received — Romilda will be in touch.',
    },
    success: {
      title: 'Thank you, {name}.',
      body: 'Your enquiry is with Romilda. She reads every message personally and will reply to {email} within two working days.',
      whileYouWait: 'While you wait',
      continueWhatsapp: 'Continue on WhatsApp',
      sendAnother: 'Send another enquiry',
    },
    faq: [
      {
        q: 'What happens after I send this?',
        a: 'Romilda reads every enquiry personally and replies within two working days with next steps or a proposed time to talk.',
      },
      {
        q: 'What does the consultation cover?',
        a: 'Your positioning, your audience, what has held growth back, and an honest view of which service would move the needle first.',
      },
      {
        q: 'Do you share pricing before the call?',
        a: 'Scope is tailored to your brand, so pricing is discussed in the consultation once we know what the work actually requires.',
      },
    ],
    aiTeaser: {
      title: 'Want an instant read first?',
      body:
        'Ask Romilda AI — bottom right of your screen — for a first-pass strategic direction in under a minute.',
    },
    bottom: {
      prefix: 'Prefer to browse first?',
      servicesLink: 'See the services',
      aboutLink: 'read the story',
    },
  },

  notFound: {
    title: 'This page has moved on.',
    body: 'The page you were looking for isn’t here. Let’s get you back to something useful.',
    home: 'Back to home',
  },

  ctaBlock: {
    eyebrow: 'Let’s begin',
    title: 'Ready to elevate your brand?',
    body:
      'Book a consultation and let’s build a strategy tailored to your vision — your audience, your aesthetic, your ambition.',
  },

  widget: {
    launcherTitle: 'Romilda AI',
    launcherSubtitle: 'Instant strategy',
    launcherAria: 'Open Romilda AI — instant strategy guidance',
    panelAria: 'Romilda AI',
    welcomeTitle: 'Welcome to Romilda AI.',
    welcomeBody:
      'Tell me a little about your brand, and I’ll help you identify your next growth opportunity.',
    startHere: 'Or start here',
    thinking: 'Thinking',
    placeholder: 'Tell me about your brand…',
    inputAria: 'Message Romilda AI',
    sendAria: 'Send message',
    disclaimer: 'Strategic guidance, not a substitute for a full consultation.',
    resetAria: 'Start a new conversation',
    closeAria: 'Close Romilda AI',
    emailInstead: 'Email us instead',
    unavailable:
      'Romilda AI is momentarily unavailable. Please try again, or email {email}.',
    quickActions: [
      {
        id: 'audit',

        label: 'Audit my strategy',
        prompt:
          "I'd like an audit of my current marketing strategy. Where should we start?",
      },
      {
        id: 'ideas',

        label: 'Give me content ideas',
        prompt: 'I need fresh content ideas for my brand.',
      },
      {
        id: 'website',

        label: 'Website & automation',
        prompt:
          'I need a new website, or I want to automate parts of my business with AI. What is possible?',
      },
      {
        id: 'instagram',

        label: 'Improve my Instagram',
        prompt: 'How can I improve my Instagram presence?',
      },
      {
        id: 'branding',

        label: 'Help with my branding',
        prompt: 'I need help with my branding and visual identity.',
      },
      {
        id: 'audience',

        label: 'Grow my audience',
        prompt: 'I want to grow my audience. What should I focus on?',
      },
      {
        id: 'services',

        label: 'Explore your services',
        prompt: 'Which of your services would be the best fit for me?',
      },
    ],
    lead: {
      eyebrow: 'Continue with Romilda',
      title: 'Shall I pass your details to Romilda?',
      body: 'She’ll follow up with a tailored next step — no automated sequences.',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'Email address',
      focusPlaceholder: 'What are you focused on?',
      dismissAria: 'Dismiss',
      submit: 'Send to Romilda',
      error: 'Please add your name and a valid email.',
      failed: 'We could not save that just now. Please try again.',
      toast: 'Thank you — Romilda will be in touch personally.',
      saved: 'Your details are with Romilda. She’ll be in touch personally.',
    },
  },

  admin: {
    login: {
      eyebrow: 'Internal area',
      title: 'Lead dashboard',
      body: 'Enter the dashboard password to view captured leads and AI conversations.',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Dashboard password',
      submit: 'Sign in',
      signingIn: 'Signing in…',
      wrong: 'That password is not correct.',
      failed: 'Could not sign in. Please try again.',
      empty: 'Please enter the password.',
    },
    eyebrow: 'Internal',
    title: 'Captured leads',
    subtitle:
      'Enquiries from the consultation form and details volunteered inside Romilda AI.',
    signOut: 'Sign out',
    refresh: 'Refresh',
    exportCsv: 'Export CSV',
    exporting: 'Exporting…',
    exportFailed: 'Could not export the CSV.',
    stats: {
      total: 'Total leads',
      unread: 'New / unread',
      form: 'From the form',
      ai: 'From Romilda AI',
    },
    filters: {
      all: 'All',
      new: 'New',
      form: 'Contact form',
      ai: 'Romilda AI',
    },
    searchPlaceholder: 'Search name, email, brand…',
    searchAria: 'Search leads',
    loadError: 'Could not load leads. Please try again.',
    empty: {
      title: 'No leads yet',
      body:
        'Enquiries from the consultation form and details captured by Romilda AI will appear here.',
    },
    emptyFiltered: {
      title: 'Nothing matches that filter',
      body: 'Try a different filter or clear the search.',
    },
    lead: {
      unnamed: 'Unnamed visitor',
      sourceAi: 'Romilda AI',
      sourceForm: 'Contact form',
      interest: 'Interest',
      challenge: 'Their challenge',
      summary: 'AI summary',
      platform: 'Platform',
      budget: 'Budget range',
      timeline: 'Timeline',
      message: 'Message',
      viewDetails: 'View details',
      newBadge: 'New',
    },
    detail: {
      title: 'Lead details',
      contact: 'Contact',
      noEmail: 'No email provided',
      conversation: 'Romilda AI conversation',
      conversationEmpty: 'This lead came from the contact form — no AI conversation.',
      conversationLoading: 'Loading conversation…',
      visitor: 'Visitor',
      assistant: 'Romilda AI',
      messagesCount: '{count} messages',
      close: 'Close',
      emailAction: 'Reply by email',
      whatsappAction: 'Open WhatsApp',
      statusLabel: 'Status',
      copyEmail: 'Copy email',
      copied: 'Email copied',
    },
    status: {
      new: 'New',
      read: 'Read',
      contacted: 'Contacted',
      archived: 'Archived',
    },
    statusUpdated: 'Status updated',
    statusFailed: 'Could not update the status.',
  },

  seo: {
    '/': {
      title:
        'Romilda Marketing Hub | Premium Content Strategy, Social Media Growth & Branding',
      description:
        'Romilda Marketing Hub is a premium digital marketing agency for brands with taste — content strategy, social media management, visual identity and digital strategy. 500,000+ views and 160,000+ accounts reached monthly. Book a consultation.',
      keywords:
        'digital marketing agency, content strategy, social media management, visual identity, branding agency, Instagram growth, editorial marketing, premium marketing agency, Romilda Marketing Hub',
    },
    '/services': {
      title:
        'Services | Social Media Management, Content Strategy, Branding & Digital Strategy',
      description:
        'Five disciplines, one elevated brand: social media management, content strategy and aesthetic, visual identity and branding, data-driven digital strategy, plus custom websites and AI automation. See who each service is for, expected outcomes and what is included.',
      keywords:
        'social media management services, content strategy agency, brand identity design, logo and brand guidelines, digital marketing strategy, Instagram content strategy, custom website development, AI automation, business automation agency',
    },
    '/about': {
      title: 'About Romilda | Founder, Strategist & Editorial Art Director',
      description:
        'Meet Romilda — founder of Romilda Marketing Hub. Structured marketing strategy blended with high-fashion, editorial art direction, for brands that want taste and results in the same room.',
      keywords:
        'Romilda Bahollari, marketing strategist, editorial art direction, personal brand agency, founder-led marketing studio',
    },
    '/contact': {
      title: 'Book a Consultation | Contact Romilda Marketing Hub',
      description:
        'Book a consultation with Romilda Marketing Hub. Share your brand and your biggest current challenge, and receive a tailored strategic direction. Reach us by form, email or WhatsApp.',
      keywords:
        'book marketing consultation, contact marketing agency, marketing strategy call, hire social media manager',
    },
    '/leads': {
      title: 'Leads | Romilda Marketing Hub',
      description: 'Internal leads overview for Romilda Marketing Hub.',
      noindex: true,
    },
    '*': {
      title: 'Page not found | Romilda Marketing Hub',
      description:
        'The page you were looking for is not here. Return to Romilda Marketing Hub to explore our services or book a consultation.',
      noindex: true,
    },
  },

  ldDescription:
    'Premium digital marketing agency providing tailored marketing solutions, content strategy, social media growth, visual identity and brand development.',
};

export default en;
